import { AnimatePresence, motion } from 'framer-motion';
import { Activity } from 'lucide-react';

import { useTranslation } from '@infra/i18n';

type LoadingOverlayProps = {
  isLoading: boolean;
  copy?: {
    title: string;
    titleHighlight: string;
    message: string;
  };
};

export function LoadingOverlay({ isLoading, copy }: LoadingOverlayProps) {
  const { t } = useTranslation();

  const fallbackCopy = {
    title: t('landing:loading.title'),
    titleHighlight: t('landing:loading.titleHighlight'),
    message: t('landing:loading.message'),
  };
  const resolvedCopy = copy ?? fallbackCopy;
  const brandTitle = resolvedCopy.title;
  const brandHighlight = resolvedCopy.titleHighlight;
  const brandText = brandTitle.includes(brandHighlight)
    ? brandTitle.replace(brandHighlight, '').trim()
    : brandTitle;

  return (
    <AnimatePresence>
      {isLoading && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, filter: 'blur(10px)' }}
          transition={{ duration: 0.8, ease: 'easeInOut' }}
          className="fixed inset-0 z-[100] bg-background flex flex-col items-center justify-center overflow-hidden"
        >
          <div className="relative">
            <div className="absolute inset-0 z-0 opacity-20 pointer-events-none">
              <div className="w-full h-full bg-[linear-gradient(transparent_0%,_var(--loading-scanline)_50%,_transparent_100%)] bg-[length:100%_4px]" />
            </div>

            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="relative z-10 flex flex-col items-center gap-6"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
                <Activity className="w-16 h-16 text-primary relative z-10" />
              </div>

              <h1 className="text-3xl font-bold tracking-tight text-foreground uppercase">
                {brandText}
                <span className="text-primary">{brandHighlight}</span>
              </h1>

              <div className="w-48 h-1 bg-muted rounded-full overflow-hidden relative">
                <motion.div
                  className="absolute top-0 left-0 h-full bg-primary"
                  initial={{ width: '0%' }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 1.2, ease: 'easeInOut' }}
                />
                <motion.div
                  className="absolute top-0 left-0 h-full w-full bg-gradient-to-r from-transparent via-tone-glass/50 to-transparent"
                  animate={{ x: ['-100%', '100%'] }}
                  transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                />
              </div>

              <p className="text-sm text-muted-foreground">{resolvedCopy.message}</p>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
